import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { BaseCommentRequest } from '../../../../models/base/base-comment-request';
import { BaseComment } from '../../../../models/base/base-comment';
import { BaseComponent } from '../../../../models/base/base-component';
import { UserTaggingDropdownItem } from '../../../../models/shared/user-tagging-dropdown-item';
import { ScreenService } from '../../../../services/screen-service.service';
import { ReactiveFormGroupComponent, ReactiveFormModule } from '@csspension/reactive-form';
import { CommonModule } from '@angular/common';
import { LoadingShimmerModule } from 'loading-shimmer';
import { CommentListComponent } from './comment-list/comment-list.component';
import { UserProfileBadgeComponent } from '../user-profile-badge/user-profile-badge.component';
import { DomSanitizer } from '@angular/platform-browser';
import { UserDomainModel } from '../../../../domainModels/user-domain-model';
import { CommentsService } from '../../services/comments.service';

@Component({
  selector: 'app-comments-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormModule, LoadingShimmerModule, CommentListComponent, UserProfileBadgeComponent],
  templateUrl: './comments-form.component.html',
  styleUrls: ['./comments-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentsFormComponent extends BaseComponent implements OnChanges, OnDestroy, AfterViewInit {
  private screenService = inject(ScreenService);
  private userDomainModel = inject(UserDomainModel);
  private commentsService = inject(CommentsService);
  public sanitizer = inject(DomSanitizer);

  private currentUserId$ = this.userDomainModel.user$.pipe(map(u => u?.id ?? null));

  @Input() isLoading: boolean = false;
  @Input() comments: BaseComment[] = [];
  @Input() commentReq: BaseCommentRequest = new BaseCommentRequest();
  @Input() dropdownItems: UserTaggingDropdownItem[] = [];
  @Input() visibilityLabel: string = '';
  @Input() headerHeight: string = '0rem';
  @Input() windowOpened: EventEmitter<void> = new EventEmitter<void>();

  @Output() newComment = new EventEmitter<BaseCommentRequest>();
  @Output() filterText = new EventEmitter<string>();
  @Output() commentValueChanged = new EventEmitter<string>();

  @ViewChild('form') form!: ReactiveFormGroupComponent;
  @ViewChild('unreadCommentsScroll', { static: true }) unreadCommentsScroll!: ElementRef<HTMLDivElement>;

  private _showCommentWindow = new BehaviorSubject<boolean>(false);
  public readonly showCommentWindow$ = this._showCommentWindow as Observable<boolean>;

  private _commentReq = new BehaviorSubject<BaseCommentRequest>(new BaseCommentRequest());
  public commentReq$ = this._commentReq as Observable<BaseCommentRequest>;

  private _comments = new BehaviorSubject<BaseComment[]>([]);
  private _readSnapshotIds = new BehaviorSubject<Set<string> | null>(null);

  public readComments$ = combineLatest([this._comments, this._readSnapshotIds, this.currentUserId$]).pipe(
    map(([comments, snapshot, userId]) => {
      return comments.filter(c => this.isConsideredRead(c, snapshot, userId));
    })
  );

  public unreadComments$ = combineLatest([this._comments, this._readSnapshotIds, this.currentUserId$]).pipe(
    map(([comments, snapshot, userId]) => {
      return comments.filter(c => !this.isConsideredRead(c, snapshot, userId));
    })
  );

  public totalNumberOfComments$ = this._comments.pipe(map(c => c?.length ?? 0));

  public readonly maxCommentLength = 1000;
  private _commentLength = new BehaviorSubject<number>(0);
  public commentLength$ = this._commentLength.asObservable();
  public commentExceedsMaxLength$ = this._commentLength.pipe(
    map(length => length > this.maxCommentLength),
    distinctUntilChanged()
  );

  public filterDropdown(text: string): void {
    this.filterText.emit(text);
  }

  public onCommentValueChange(comment: string): void {
    this._commentLength.next(comment?.length ?? 0);
    this.commentValueChanged.emit(comment);
  }

  public addComment(comment: BaseCommentRequest): void {
    this.unreadComments$.once(unread => unread.forEach(c => this.commentsService.setCommentScrolledIntoView(c)));
    this._comments.once(comments => this._readSnapshotIds.next(new Set(comments.map(c => c.id))));
    this._commentLength.next(0);
    this.newComment.emit(comment);
  }

  public submitForm() {
    this.form.submitForm();
  }

  private isConsideredRead(comment: BaseComment, snapshot: Set<string> | null, userId: number | null): boolean {
    if (userId !== null && Number(comment.authorId) === userId) return true;
    return snapshot ? snapshot.has(comment.id) : !!comment.readDate;
  }

  private scrollToUnreadComments(): void {
    setTimeout(() => this.unreadCommentsScroll.nativeElement.scrollIntoView({ behavior: 'smooth' }), 250);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.comments) {
      this._comments.next(this.comments ?? []);
    }
    if (!!changes.commentReq) {
      this._commentReq.next(this.commentReq);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.screenService.enableScroll();
  }

  setupBindings(): void {
    this.windowOpened.subscribeWhileAlive({
      owner: this,
      next: () => {
        this._comments
          .pipe(
            filter(comments => comments?.length > 0),
            take(1)
          )
          .subscribe(comments => {
            const snapshot = new Set(comments.filter(c => !!c.readDate).map(c => c.id));
            this._readSnapshotIds.next(snapshot);
            this.unreadComments$.once(value => {
              if (value.length) this.scrollToUnreadComments();
            });
          });
      }
    });
  }

  setupViews(): void {}
}
